// Small rendering/formatting utilities.

// Choose a "nice" step for price gridlines given a target pixel spacing.
export function nicePriceStep(range: number, targetTicks: number): number {
  if (range <= 0 || !isFinite(range)) return 1;
  const rough = range / Math.max(1, targetTicks);
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / pow;
  let mult: number;
  if (norm < 1.5) mult = 1;
  else if (norm < 3) mult = 2;
  else if (norm < 7) mult = 5;
  else mult = 10;
  return mult * pow;
}

export function formatPrice(p: number, decimals = 2): string {
  if (!isFinite(p)) return "";
  return p.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function pad2(n: number): string {
  return n < 10 ? "0" + n : "" + n;
}

// ---------------------------------------------------------------------------
// New York timezone (America/New_York, DST-aware). The MNQ data is native to
// NY market hours (first bar 13:30 UTC = 09:30 ET), so all display uses NY time.
// ---------------------------------------------------------------------------
const NY_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const nyOffCache = new Map<number, number>();

// seconds to add to a unix time so that a UTC-reading Date shows NY wall clock
function nyOffsetSec(t: number): number {
  const bucket = Math.floor(t / 3600); // offset is constant within an hour (except DST switch hour)
  const cached = nyOffCache.get(bucket);
  if (cached !== undefined) return cached;
  const parts = NY_FMT.formatToParts(new Date(t * 1000));
  const get = (ty: string) => +(parts.find((p) => p.type === ty)?.value ?? "0");
  const asUTC = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second")) / 1000;
  const off = asUTC - t;
  if (nyOffCache.size > 8000) nyOffCache.clear();
  nyOffCache.set(bucket, off);
  return off;
}

// A Date whose getUTC* fields equal the NY wall clock for unix time t.
export function nyDate(t: number): Date {
  return new Date((t + nyOffsetSec(t)) * 1000);
}

// Convert NY wall-clock components -> unix seconds (used by Go-to-Date input).
export function nyWallToUnix(y: number, mo: number, d: number, h: number, mi: number, s: number): number {
  const guess = Date.UTC(y, mo - 1, d, h, mi, s) / 1000;
  return guess - nyOffsetSec(guess);
}

export function formatTimeOfDay(t: number): string {
  const d = nyDate(t);
  return pad2(d.getUTCHours()) + ":" + pad2(d.getUTCMinutes());
}

export function formatTimeFull(t: number): string {
  const d = nyDate(t);
  return pad2(d.getUTCHours()) + ":" + pad2(d.getUTCMinutes()) + ":" + pad2(d.getUTCSeconds());
}

export function formatDateShort(t: number): string {
  const d = nyDate(t);
  return MONTHS[d.getUTCMonth()] + " " + d.getUTCDate();
}

export function formatMonthYear(t: number): string {
  const d = nyDate(t);
  return MONTHS[d.getUTCMonth()] + " '" + pad2(d.getUTCFullYear() % 100);
}

export function formatDateTime(t: number): string {
  const d = nyDate(t);
  return (
    MONTHS[d.getUTCMonth()] +
    " " +
    d.getUTCDate() +
    ", " +
    d.getUTCFullYear() +
    "  " +
    pad2(d.getUTCHours()) +
    ":" +
    pad2(d.getUTCMinutes()) +
    ":" +
    pad2(d.getUTCSeconds()) +
    " ET"
  );
}

// crosshair time bubble label (adaptive by timeframe)
export function formatCrosshairTime(t: number, tfSeconds: number): string {
  const d = nyDate(t);
  const date = MONTHS[d.getUTCMonth()] + " " + d.getUTCDate() + " '" + pad2(d.getUTCFullYear() % 100);
  if (tfSeconds >= 86400) return date;
  const time = tfSeconds < 60 ? formatTimeFull(t) : formatTimeOfDay(t);
  return date + "  " + time;
}

export function isNewDay(prev: number, cur: number): boolean {
  const a = nyDate(prev);
  const b = nyDate(cur);
  return a.getUTCFullYear() !== b.getUTCFullYear() || a.getUTCMonth() !== b.getUTCMonth() || a.getUTCDate() !== b.getUTCDate();
}

export function isNewMonth(prev: number, cur: number): boolean {
  const a = nyDate(prev);
  const b = nyDate(cur);
  return a.getUTCFullYear() !== b.getUTCFullYear() || a.getUTCMonth() !== b.getUTCMonth();
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// ease in-out cubic
export function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
